import {
	useMutation,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import type {
	MetaAdsCreateScheduleDto,
	MetaAdsCreateTemplateDto,
	MetaAdsGenerateReportDto,
	MetaAdsReportBranding,
	MetaAdsUpdateScheduleDto,
	MetaAdsUpdateTemplateDto,
} from "shared";
import { Api } from "../api.config";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { useCompany } from "../company/useCompany";
import { QueryKeys } from "../LinkGen";

export const useReportTemplates = () => {
	const company = useCompany();
	return useSuspenseQuery({
		queryKey: QueryKeys.Integrations.MetaAds.ReportTemplates(company.id),
		queryFn: () => Api.GetMetaAdsReportTemplates().then((r) => r.data),
	});
};

export const useReportSchedules = () => {
	const company = useCompany();
	return useSuspenseQuery({
		queryKey: QueryKeys.Integrations.MetaAds.ReportSchedules(company.id),
		queryFn: () => Api.GetMetaAdsReportSchedules().then((r) => r.data),
	});
};

export const useReportBranding = () => {
	const company = useCompany();
	return useSuspenseQuery({
		queryKey: QueryKeys.Integrations.MetaAds.ReportBranding(company.id),
		queryFn: () => Api.GetMetaAdsReportBranding().then((r) => r.data),
	});
};

export const useReportActions = () => {
	const company = useCompany();
	const queryClient = useQueryClient();
	const emit = useAlertEmit();

	const createTemplate = useMutation({
		mutationFn: (dto: MetaAdsCreateTemplateDto) =>
			Api.CreateMetaAdsReportTemplate(dto).then((r) => r.data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.MetaAds.ReportTemplates(company.id),
			});
			emit({ type: AlertType.SUCCESS, content: "Template created." });
		},
		onError: () => {
			emit({ type: AlertType.ERROR, content: "Failed to create template." });
		},
	});

	const updateTemplate = useMutation({
		mutationFn: ({ id, dto }: { id: number; dto: MetaAdsUpdateTemplateDto }) =>
			Api.UpdateMetaAdsReportTemplate(id, dto).then((r) => r.data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.MetaAds.ReportTemplates(company.id),
			});
			emit({ type: AlertType.SUCCESS, content: "Template updated." });
		},
		onError: () => {
			emit({ type: AlertType.ERROR, content: "Failed to update template." });
		},
	});

	const deleteTemplate = useMutation({
		mutationFn: (id: number) =>
			Api.DeleteMetaAdsReportTemplate(id).then((r) => r.data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.MetaAds.ReportTemplates(company.id),
			});
			emit({ type: AlertType.SUCCESS, content: "Template deleted." });
		},
		onError: () => {
			emit({ type: AlertType.ERROR, content: "Failed to delete template." });
		},
	});

	const createSchedule = useMutation({
		mutationFn: (dto: MetaAdsCreateScheduleDto) =>
			Api.CreateMetaAdsReportSchedule(dto).then((r) => r.data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.MetaAds.ReportSchedules(company.id),
			});
			emit({ type: AlertType.SUCCESS, content: "Schedule created." });
		},
		onError: () => {
			emit({ type: AlertType.ERROR, content: "Failed to create schedule." });
		},
	});

	const updateSchedule = useMutation({
		mutationFn: ({ id, dto }: { id: number; dto: MetaAdsUpdateScheduleDto }) =>
			Api.UpdateMetaAdsReportSchedule(id, dto).then((r) => r.data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.MetaAds.ReportSchedules(company.id),
			});
			emit({ type: AlertType.SUCCESS, content: "Schedule updated." });
		},
		onError: () => {
			emit({ type: AlertType.ERROR, content: "Failed to update schedule." });
		},
	});

	const deleteSchedule = useMutation({
		mutationFn: (id: number) =>
			Api.DeleteMetaAdsReportSchedule(id).then((r) => r.data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.MetaAds.ReportSchedules(company.id),
			});
			emit({ type: AlertType.SUCCESS, content: "Schedule deleted." });
		},
		onError: () => {
			emit({ type: AlertType.ERROR, content: "Failed to delete schedule." });
		},
	});

	const updateBranding = useMutation({
		mutationFn: (dto: MetaAdsReportBranding) =>
			Api.UpdateMetaAdsReportBranding(dto).then((r) => r.data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.MetaAds.ReportBranding(company.id),
			});
			emit({ type: AlertType.SUCCESS, content: "Branding saved." });
		},
		onError: () => {
			emit({ type: AlertType.ERROR, content: "Failed to save branding." });
		},
	});

	const generateReport = useMutation({
		mutationFn: (dto: MetaAdsGenerateReportDto) =>
			Api.GenerateMetaAdsReport(dto).then((r) => r.data),
	});

	const sendTestReport = useMutation({
		mutationFn: (dto: MetaAdsGenerateReportDto) =>
			Api.SendMetaAdsTestReport(dto).then((r) => r.data),
		onSuccess: () => {
			emit({
				type: AlertType.SUCCESS,
				content: "Test report sent to your email.",
			});
		},
		onError: () => {
			emit({ type: AlertType.ERROR, content: "Failed to send test report." });
		},
	});

	return {
		createTemplate,
		updateTemplate,
		deleteTemplate,
		createSchedule,
		updateSchedule,
		deleteSchedule,
		updateBranding,
		generateReport,
		sendTestReport,
	};
};
